import React, { useEffect, useState, JSX } from "react";
import { createRoot } from "react-dom/client";
import "react-loading-skeleton/dist/skeleton.css";
import "./common/styles/main.scss";
import reportWebVitals from "./reportWebVitals";
import { registerSW } from "virtual:pwa-register";
import { Provider } from "react-redux";
import { store } from "./app/store";
import {
    AuthProvider,
    TAuthConfig,
    TRefreshTokenExpiredEvent,
} from "react-oauth2-code-pkce";
import AuthWrapper from "./app/AuthWrapper";
import { useNetworkStatus } from "./common/hooks/useNetworkStatus";
import { dataSyncService } from "./common/services/dataSyncService";
import { setupTracing } from "./common/utils/tempoTracing";

setupTracing();

const container = document.getElementById("root");
const root = createRoot(container!);
const appUrl = import.meta.env.VITE_APP_URL as string | undefined;
const baseAppUrl =
    appUrl && /^https?:\/\//i.test(appUrl) ? appUrl : window.location.origin;
const redirectUri = baseAppUrl.replace(/\/+$/, "") + "/";
const authConfig: TAuthConfig = {
    clientId: import.meta.env.VITE_CLIENT_ID,
    authorizationEndpoint: import.meta.env.VITE_AUTH_URL + "/oauth/authorize",
    tokenEndpoint: import.meta.env.VITE_AUTH_URL + "/oauth/token",
    redirectUri: redirectUri,
    logoutEndpoint: import.meta.env.VITE_AUTH_URL + "/oauth/logout",
    loginMethod: "redirect",
    storage: "local",
    extraAuthParameters: { prompt: "login" },
    autoLogin: false,
    onRefreshTokenExpire: async (event: TRefreshTokenExpiredEvent) => {
        try {
            const hasSynchronizedData =
                await dataSyncService.hasSynchronizedData();
            if (!hasSynchronizedData) {
                const shouldRefresh = window.confirm(
                    "Session expired. Refresh page to continue using the site?",
                );
                if (shouldRefresh) {
                    event.logIn();
                }
            }
        } catch (_error) {
            console.warn(
                "Could not check synchronized data, showing session expired popup",
            );
            const shouldRefresh = window.confirm(
                "Session expired. Refresh page to continue using the site?",
            );
            if (shouldRefresh) {
                event.logIn();
            }
        }
    },
};

const ConditionalAuthProvider = ({
    children,
}: {
    children: React.ReactNode;
}): JSX.Element => {
    const { isOffline } = useNetworkStatus();
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsInitialized(true);
        }, 100);

        return () => {
            clearTimeout(timer);
        };
    }, []);

    if (!isInitialized) {
        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                    backgroundColor: "#f8f9fa",
                }}
            >
                <div>Loading...</div>
            </div>
        );
    }

    if (isOffline) {
        return <>{children}</>;
    }

    return <AuthProvider authConfig={authConfig}>{children}</AuthProvider>;
};

root.render(
    <React.StrictMode>
        <ConditionalAuthProvider>
            <Provider store={store}>
                <AuthWrapper />
            </Provider>
        </ConditionalAuthProvider>
    </React.StrictMode>,
);

const updateSW = registerSW({
    onNeedRefresh() {
        if (confirm("New content available, reload?")) {
            updateSW(true);
        }
    },

    onOfflineReady() {
        console.log("App ready to work offline");
    },
});

// Web Vitals are collected in production builds only (see reportWebVitals.ts).
reportWebVitals((metric) => {
    console.log("[Web Vitals]", metric);
});
